import { AlertTriangle, CheckCircle2, Copy, Cpu, ExternalLink, KeyRound, Loader2, RefreshCw, Settings2, XCircle } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { usePlatformApi } from "../hooks/usePlatformApi";
import type { Organization } from "../types/platform";
import type { EffectiveTier, LlmProfileResolution, LlmProvidersStatus } from "../types/llm-providers";
// Active-first ordering is shared with RuntimeFleet's org filter — the
// org dropdown here is a read-only resolver-diagnostic surface, so
// inactive/terminated orgs are de-prioritised + status-labelled (below),
// never dropped.
import { sortOrgsActiveFirst } from "../lib/org-order";
import { CopyProviderSettingsDialog } from "../components/platform/CopyProviderSettingsDialog";

/** A small "edit this elsewhere" backlink for the read-only diagnostic
 *  panels — the whole page shows resolver OUTPUT; the actual editors live on
 *  the org's AI Providers tab (credentials/aliases/OAuth) or Platform
 *  Settings (the catalog). Every panel says where to change what it shows. */
function EditLink({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="ml-auto inline-flex items-center gap-1.5 rounded-md border border-slate-300 px-2.5 py-1 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
    >
      <Settings2 className="h-3.5 w-3.5" />
      {label}
      <ExternalLink className="h-3 w-3" />
    </button>
  );
}

/**
 * LexPortal "LLM Providers" page — read-only view of the resolver's
 * decisions for a selected org. Backs by `/api/llm-providers/:orgId/status`.
 *
 * Two panels:
 *  - **Enabled providers** — which provider has a configured credential
 *    in the org's `runtimeConfig.llm` block (anthropic / openai / google / xai),
 *    plus the auth mode for anthropic (oauth vs api-key).
 *  - **Resolved profiles** — what each profile (MAX/MED/LOW + custom)
 *    resolves to right now, with the chosen provider+model and the
 *    policy block (thinkingDefault / maxOutputTokens / temperature).
 *    When no provider can satisfy a profile, the per-provider rejection
 *    reasons are displayed.
 */
export function LlmProviders() {
  const api = usePlatformApi();
  const navigate = useNavigate();
  const { orgId: urlOrgId } = useParams<{ orgId?: string }>();

  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [orgsLoading, setOrgsLoading] = useState(true);
  const [orgsError, setOrgsError] = useState<string | null>(null);

  // URL is the source of truth for org selection (plan §7 deep-link
  // `/llm-providers/:orgId`). When unset, we auto-select the first org
  // once `loadOrgs` resolves.
  const selectedOrgId = urlOrgId ?? null;
  const [status, setStatus] = useState<LlmProvidersStatus | null>(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [statusError, setStatusError] = useState<string | null>(null);
  // §8.9 rewire — the org's AUTHORITATIVE per-alias ladder (effectiveTiers
  // from the org-scoped llm-status). Fail-soft: null keeps the section
  // hidden and the platform-catalog diagnostic below still renders.
  const [ladder, setLadder] = useState<EffectiveTier[] | null>(null);
  const [copyOpen, setCopyOpen] = useState(false);

  const loadOrgs = useCallback(async () => {
    try {
      setOrgsLoading(true);
      const res = await api.listOrgs({ limit: 100 });
      const sorted = sortOrgsActiveFirst(res.data);
      setOrgs(sorted);
      setOrgsError(null);
      // Auto-select the first ACTIVE org (sorted[0]) rather than
      // whatever order the API happened to return — avoids defaulting
      // the diagnostic onto a terminated test org.
      if (!urlOrgId && sorted.length > 0) {
        navigate(`/llm-providers/${sorted[0]!.orgId}`, { replace: true });
      }
    } catch (e) {
      setOrgsError(e instanceof Error ? e.message : "Failed to load organizations");
    } finally {
      setOrgsLoading(false);
    }
  }, [api, urlOrgId, navigate]);

  const loadStatus = useCallback(
    async (orgId: string) => {
      try {
        setStatusLoading(true);
        const [res, orgRes] = await Promise.all([
          api.getLlmProvidersStatus(orgId),
          api.getOrgLlmStatus(orgId).catch(() => null),
        ]);
        setStatus(res.data);
        setLadder(orgRes?.data.effectiveTiers ?? null);
        setStatusError(null);
      } catch (e) {
        setStatus(null);
        setLadder(null);
        setStatusError(e instanceof Error ? e.message : "Failed to load LLM provider status");
      } finally {
        setStatusLoading(false);
      }
    },
    [api],
  );

  useEffect(() => {
    loadOrgs();
    // loadOrgs is stable enough; deliberately don't depend on urlOrgId here
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedOrgId) loadStatus(selectedOrgId);
  }, [selectedOrgId, loadStatus]);

  const profileEntries = useMemo(
    () => (status ? Object.entries(status.resolvedProfiles) : []),
    [status],
  );
  // §8.9 — group thinking variants under their BASE tier (R-15: a composite
  // `MAX:high` always resolves to the same provider/model as `MAX`, only the
  // thinking level differs). One group per base; variants list the overlay
  // levels as sub-detail. Tolerates both a flat composite list (legacy) and a
  // base-only list.
  const profileGroups = useMemo(() => {
    const groups = new Map<
      string,
      { base: string; baseRes: LlmProfileResolution | null; thinkings: string[] }
    >();
    for (const [name, res] of profileEntries) {
      const idx = name.indexOf(":");
      const base = idx === -1 ? name : name.slice(0, idx);
      const thinking = idx === -1 ? null : name.slice(idx + 1);
      let g = groups.get(base);
      if (!g) {
        g = { base, baseRes: null, thinkings: [] };
        groups.set(base, g);
      }
      if (thinking === null) g.baseRes = res;
      else g.thinkings.push(thinking);
      // If there's no bare-base key (only composites), adopt the first
      // variant's resolution for the base row (same provider/model per R-15).
      if (g.baseRes === null && thinking !== null) g.baseRes = res;
    }
    return [...groups.values()];
  }, [profileEntries]);
  const providerEntries = useMemo(
    () => (status ? Object.entries(status.enabledProviders) : []),
    [status],
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="max-w-3xl">
          <h1 className="text-2xl font-semibold tracking-tight">LLM Providers</h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            <strong className="text-slate-600 dark:text-slate-300">Read-only diagnostic.</strong>{" "}
            Shows what each profile/alias resolves to for the selected org right
            now. Nothing here is edited on this page — use the{" "}
            <span className="font-medium text-slate-600 dark:text-slate-300">Edit…</span>{" "}
            link on each panel: this org&apos;s credentials, aliases &amp; OAuth
            live on its <span className="font-medium">AI Providers</span> editor;
            the platform catalog lives in <span className="font-medium">Platform Settings</span>.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {selectedOrgId && (
            <button
              type="button"
              onClick={() => setCopyOpen(true)}
              data-testid="llm-providers-copy-settings"
              className="inline-flex items-center gap-2 rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              <Copy className="h-4 w-4" />
              Copy settings…
            </button>
          )}
          {selectedOrgId && (
            <button
              type="button"
              onClick={() => navigate(`/orgs/${selectedOrgId}/config`)}
              className="inline-flex items-center gap-2 rounded-md border border-indigo-300 bg-indigo-50 px-3 py-1.5 text-sm font-medium text-indigo-700 transition hover:bg-indigo-100 dark:border-indigo-700 dark:bg-indigo-950 dark:text-indigo-300 dark:hover:bg-indigo-900"
            >
              <Settings2 className="h-4 w-4" />
              Open AI Providers editor
              <ExternalLink className="h-3.5 w-3.5" />
            </button>
          )}
          <button
            type="button"
            onClick={() => selectedOrgId && loadStatus(selectedOrgId)}
            disabled={!selectedOrgId || statusLoading}
            className="inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            {statusLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            Refresh
          </button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Organization</CardTitle>
        </CardHeader>
        <CardContent>
          {orgsLoading ? (
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading organizations…
            </div>
          ) : orgsError ? (
            <p className="text-sm text-rose-600">{orgsError}</p>
          ) : orgs.length === 0 ? (
            <p className="text-sm text-slate-500">No organizations available.</p>
          ) : (
            <select
              data-testid="llm-providers-org-select"
              value={selectedOrgId ?? ""}
              onChange={(e) => {
                const next = e.target.value;
                if (next) navigate(`/llm-providers/${next}`);
                else navigate("/llm-providers");
              }}
              className="w-full max-w-md rounded-md border border-slate-200 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900"
            >
              {orgs.map((org) => (
                <option key={org.orgId} value={org.orgId}>
                  {org.status === "ACTIVE"
                    ? `${org.name} (${org.orgId})`
                    : `${org.name} — ${org.status} (${org.orgId})`}
                </option>
              ))}
            </select>
          )}
        </CardContent>
      </Card>

      {statusError && (
        <Card>
          <CardContent className="py-4">
            <p className="text-sm text-rose-600">{statusError}</p>
          </CardContent>
        </Card>
      )}

      {/* Slice 3.D.3 — the authoritative chat-default. Read-only here;
          edit at /orgs/:orgId/config (the org-admin "AI Providers" tab).
          The panels below are diagnostic projections of the resolver's
          HARNESS/heartbeat selections — NOT the chat-default. */}
      <Card data-testid="llm-providers-active-model">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-sm">
            <Cpu className="h-4 w-4" /> Active chat model
            <span className="ml-1 rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
              authoritative
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!status ? (
            statusLoading ? (
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading…
              </div>
            ) : (
              <p className="text-sm text-slate-500">Select an organization above.</p>
            )
          ) : status.activeModel?.source === "org-chat-profile" ? (
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <span className="font-mono text-sm">
                  {status.activeModel.provider}/{status.activeModel.model}
                  {status.activeModel.thinkingDefault
                    ? ` · ${status.activeModel.thinkingDefault}`
                    : ""}
                </span>
                <span className="text-xs text-slate-500">
                  source:{" "}
                  <code className="font-mono">
                    models.chatProfile ("{status.activeModel.chatProfile}")
                  </code>
                </span>
              </div>
              {selectedOrgId && (
                <button
                  type="button"
                  data-testid="llm-providers-edit-chat-profile"
                  onClick={() => navigate(`/orgs/${selectedOrgId}/config`)}
                  className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
                >
                  <Settings2 className="h-3.5 w-3.5" />
                  Edit on AI Providers
                  <ExternalLink className="h-3 w-3" />
                </button>
              )}
            </div>
          ) : status.activeModel?.source === "org-field" ? (
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <span className="font-mono text-sm">
                  {status.activeModel.provider}/{status.activeModel.model}
                </span>
                <span className="text-xs text-slate-500">
                  source: <code className="font-mono">runtimeConfig.models.defaultModel</code>
                </span>
              </div>
              {selectedOrgId && (
                <button
                  type="button"
                  data-testid="llm-providers-edit-default-model"
                  onClick={() => navigate(`/orgs/${selectedOrgId}/config`)}
                  className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
                >
                  <Settings2 className="h-3.5 w-3.5" />
                  Edit default model
                  <ExternalLink className="h-3 w-3" />
                </button>
              )}
            </div>
          ) : status.activeModel?.source === "missing" ? (
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm text-amber-700 dark:text-amber-400">
                <AlertTriangle className="mr-1 inline h-4 w-4" />
                No chat-default set. The Slice 2 backfill hasn't run for this org —
                set it now via the org's AI Providers tab.
              </p>
              {selectedOrgId && (
                <button
                  type="button"
                  data-testid="llm-providers-set-default-model"
                  onClick={() => navigate(`/orgs/${selectedOrgId}/config`)}
                  className="inline-flex items-center gap-1.5 rounded-md border border-amber-400 px-3 py-1.5 text-xs font-medium text-amber-700 transition-colors hover:bg-amber-50 dark:border-amber-600 dark:text-amber-300 dark:hover:bg-amber-900/30"
                >
                  <Settings2 className="h-3.5 w-3.5" />
                  Set default model
                  <ExternalLink className="h-3 w-3" />
                </button>
              )}
            </div>
          ) : (
            <p className="text-sm text-rose-600">
              <AlertTriangle className="mr-1 inline h-4 w-4" />
              defaultModel is set but unparseable ({status.activeModel?.raw ?? "?"}).
              Edit via the org's AI Providers tab.
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-sm">
            <KeyRound className="h-4 w-4" /> Enabled providers
            {selectedOrgId && (
              <EditLink
                label="Edit credentials"
                onClick={() => navigate(`/orgs/${selectedOrgId}/config`)}
              />
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!status ? (
            statusLoading ? (
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading…
              </div>
            ) : (
              <p className="text-sm text-slate-500">Select an organization above.</p>
            )
          ) : (
            <ul className="divide-y divide-slate-100 dark:divide-slate-800">
              {providerEntries.map(([provider, p]) => (
                <li
                  key={provider}
                  data-testid={`llm-providers-provider-${provider}`}
                  className="flex items-center justify-between py-2 text-sm"
                >
                  <span className="font-medium capitalize">{provider}</span>
                  <span className="flex items-center gap-2">
                    {p.configured ? (
                      <>
                        <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                        <span className="text-emerald-700 dark:text-emerald-400">Configured</span>
                        {p.authMode && (
                          <span className="rounded bg-slate-100 px-1.5 py-0.5 text-xs uppercase tracking-wide text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                            {p.authMode}
                          </span>
                        )}
                      </>
                    ) : (
                      <>
                        <XCircle className="h-4 w-4 text-slate-400" />
                        <span className="text-slate-500">Not configured</span>
                      </>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {ladder && ladder.length > 0 && (
        <Card data-testid="llm-providers-alias-ladder">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-sm">
              <Cpu className="h-4 w-4" /> Org alias ladder
              <span className="ml-1 rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
                authoritative
              </span>
              {selectedOrgId && (
                <EditLink
                  label="Edit aliases"
                  onClick={() => navigate(`/orgs/${selectedOrgId}/config`)}
                />
              )}
            </CardTitle>
            <p className="mt-1 text-xs text-slate-500">
              The org&apos;s DYNAMIC alias table (presets + custom aliases),
              each resolved through its live PRIMARY → FALLBACK → rescue
              ladder with provider health applied — exactly what a call
              asking for that alias gets right now. Edit on the org&apos;s
              AI Providers page.
            </p>
          </CardHeader>
          <CardContent>
            <ul className="divide-y divide-slate-100 dark:divide-slate-800">
              {ladder.map((t) => (
                <li key={t.alias} className="flex items-center justify-between gap-3 py-2">
                  <div className="flex items-center gap-3">
                    <span className="w-28 shrink-0 font-mono text-sm font-semibold">{t.alias}</span>
                    <span className="font-mono text-sm">
                      {t.provider && t.model ? `${t.provider}/${t.model}` : "—"}
                    </span>
                  </div>
                  <span
                    className={
                      t.tier === "primary"
                        ? "rounded bg-emerald-100 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300"
                        : t.tier === "fallback"
                          ? "rounded bg-amber-100 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-700 dark:bg-amber-900/40 dark:text-amber-300"
                          : t.tier === "rescue"
                            ? "rounded bg-rose-100 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-rose-700 dark:bg-rose-900/40 dark:text-rose-300"
                            : t.tier === "platform"
                              ? "rounded bg-slate-100 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-slate-600 dark:bg-slate-800 dark:text-slate-300"
                              : "rounded border border-rose-400 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-rose-600 dark:text-rose-400"
                    }
                  >
                    {t.tier}
                  </span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-sm">
            <Cpu className="h-4 w-4" /> Platform-catalog resolution
            <span className="ml-1 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-slate-600 dark:bg-slate-800 dark:text-slate-300">
              pre-ladder diagnostic
            </span>
            <EditLink
              label="Edit platform catalog"
              onClick={() => navigate("/platform/settings")}
            />
          </CardTitle>
          <p className="mt-1 text-xs text-slate-500">
            What each CATALOG profile resolves to against this org&apos;s
            credentials BEFORE the org&apos;s alias table or provider health
            are considered — useful to see what a delegate-to-platform alias
            or a table-less org would get, and the policy block (thinking /
            max output) each catalog tier carries. Thinking variants share
            the tier&apos;s provider/model (R-15). The chat-default is the
            &quot;Active chat model&quot; card above.
          </p>
        </CardHeader>
        <CardContent>
          {!status ? (
            statusLoading ? (
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading…
              </div>
            ) : (
              <p className="text-sm text-slate-500">Select an organization above.</p>
            )
          ) : profileGroups.length === 0 ? (
            <p className="text-sm text-slate-500">No profiles configured.</p>
          ) : (
            <ul className="space-y-3">
              {profileGroups.map((g) => (
                <ProfileGroup key={g.base} group={g} />
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <CopyProviderSettingsDialog
        open={copyOpen}
        onOpenChange={setCopyOpen}
        orgs={orgs}
        initialSourceId={selectedOrgId ?? undefined}
        onDone={() => selectedOrgId && loadStatus(selectedOrgId)}
      />
    </div>
  );
}

/** §8.9 — one BASE tier with its thinking-overlay variants as sub-detail. */
function ProfileGroup({
  group,
}: {
  group: { base: string; baseRes: LlmProfileResolution | null; thinkings: string[] };
}) {
  return (
    <li className="space-y-1">
      {group.baseRes ? (
        <ProfileRow name={group.base} res={group.baseRes} />
      ) : (
        <div
          data-testid={`llm-providers-profile-${group.base}`}
          className="rounded-md border border-slate-200 p-3 text-sm text-slate-500 dark:border-slate-800"
        >
          {group.base}: not resolvable
        </div>
      )}
      {group.thinkings.length > 0 && (
        <div className="pl-3 text-[11px] text-slate-400 dark:text-slate-500">
          thinking overlays:{" "}
          {[...group.thinkings].sort().map((t, i) => (
            <span key={t}>
              {i > 0 ? " · " : ""}
              <code className="font-mono">{group.base}:{t}</code>
            </span>
          ))}
        </div>
      )}
    </li>
  );
}

function ProfileRow({ name, res }: { name: string; res: LlmProfileResolution }) {
  if (res.ok) {
    return (
      <div
        data-testid={`llm-providers-profile-${name}`}
        className="rounded-md border border-slate-200 p-3 dark:border-slate-800"
      >
        <div className="flex items-center justify-between">
          <span className="text-sm font-semibold uppercase tracking-wide text-slate-700 dark:text-slate-200">
            {name}
          </span>
          <span className="rounded bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300">
            Resolved
          </span>
        </div>
        <div className="mt-2 grid grid-cols-2 gap-2 text-xs text-slate-600 dark:text-slate-300 sm:grid-cols-4">
          <div>
            <div className="text-slate-400">Provider</div>
            <div className="font-medium">{res.provider}</div>
          </div>
          <div>
            <div className="text-slate-400">Model</div>
            <div className="font-medium" title={res.displayName}>{res.model}</div>
          </div>
          <div>
            <div className="text-slate-400">Thinking</div>
            <div className="font-medium">{res.policy.thinkingDefault}</div>
          </div>
          <div>
            <div className="text-slate-400">Max output</div>
            <div className="font-medium">{res.policy.maxOutputTokens ?? "—"}</div>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div
      data-testid={`llm-providers-profile-${name}`}
      className="rounded-md border border-amber-200 bg-amber-50/50 p-3 dark:border-amber-900 dark:bg-amber-950/30"
    >
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold uppercase tracking-wide text-amber-800 dark:text-amber-300">
          {name}
        </span>
        <span className="flex items-center gap-1 rounded bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900 dark:text-amber-200">
          <AlertTriangle className="h-3 w-3" />
          {res.errorCode}
        </span>
      </div>
      <div className="mt-2 text-xs text-amber-900 dark:text-amber-200">
        Considered providers: {res.consideredProviders.join(", ") || "none"}
      </div>
      {Object.keys(res.reasons).length > 0 && (
        <ul className="mt-1 space-y-0.5 text-xs text-amber-800 dark:text-amber-300">
          {Object.entries(res.reasons).map(([prov, reason]) => (
            <li key={prov}>
              <span className="font-medium">{prov}:</span> {reason}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
